import { t, pluralT, hasResumable } from './config'
import { listConversations } from './api'
import { escapeHTML } from './richText'
import { trackVera } from './tracking'
import arrowIcon from '../../../images/icons/arrow-right-black.svg'
import clockIcon from '../../../images/icons/clock-recent-midnight-black-60.svg'
import plusIcon from '../../../images/icons/plus-thin-midnight-black-60.svg'

export default function createTakeover (boxes, chat) {
  // The list (null until fetched) loads when the shopper reaches for a box, not on page view.
  let resumable = null
  let anyResumable = hasResumable()
  let latestRefresh

  boxes.forEach(bindBox)
  document.addEventListener('click', event => {
    if (!event.target.closest('.vjs-vera-search')) closePanels()
  })

  // Refreshes can overlap: only the latest one may write.
  async function refresh () {
    const request = latestRefresh = listConversations()
      .then(({ conversations }) => conversations.filter(conversation => conversation.title))
      .catch(() => [])
    const listed = await request
    if (request !== latestRefresh) return

    resumable = listed
    anyResumable = listed.length > 0
    boxes.forEach(box => {
      applyPlaceholder(box)
      if (!box.querySelector('.vjs-vera-panel').hidden) renderPanel(box)
    })
  }

  function prefetch () {
    if (anyResumable && !latestRefresh) refresh()
  }

  function bindBox (box) {
    const input = box.querySelector('.vjs-vera-search-input')
    const panel = box.querySelector('.vjs-vera-panel')

    ;['pointerenter', 'touchstart'].forEach(type => box.addEventListener(type, prefetch, { passive: true }))

    // The magnifier is a sibling of the input, not a child: the box carries the click.
    box.addEventListener('click', event => {
      if (event.target.closest('.vjs-vera-panel')) return
      activate(box)
    })

    input.addEventListener('focus', () => activate(box))

    input.addEventListener('keydown', event => {
      if (event.key !== 'Enter') return
      event.preventDefault()
      const seedText = input.value.trim()
      input.value = ''
      closePanels()
      chat.open({ seedText, entryPoint: 'search_box_enter' })
    })

    panel.addEventListener('click', event => {
      const resume = event.target.closest('.vjs-vera-continue')
      if (resume) {
        closePanels()
        return chat.open({ conversationId: resume.dataset.conversationId, entryPoint: resume.dataset.entryPoint })
      }

      if (event.target.closest('.vjs-vera-new-search')) {
        closePanels()
        chat.open({ entryPoint: 'new_search' })
      }
    })
  }

  // The list can lag the chat: reopen the loaded conversation rather than start a new one.
  function activate (box) {
    // Focus and click both land here: count the box once.
    if (!chat.isOpen && box.querySelector('.vjs-vera-panel').hidden) {
      trackVera('Search Box Clicked', { has_resumable_conversation: anyResumable })
    }

    prefetch()
    if (!anyResumable) {
      box.querySelector('.vjs-vera-search-input').blur()
      return chat.open({ conversationId: chat.conversation?.id, entryPoint: 'search_box' })
    }

    closePanels()
    renderPanel(box)
    box.querySelector('.vjs-vera-panel').hidden = false
    box.querySelector('.vjs-vera-search-input').focus()
  }

  function closePanels () {
    boxes.forEach(box => {
      box.querySelector('.vjs-vera-panel').hidden = true
    })
  }

  function applyPlaceholder (box) {
    const input = box.querySelector('.vjs-vera-search-input')
    input.placeholder = anyResumable ? t('resume_placeholder') : t('search_placeholder')
    input.readOnly = !anyResumable
  }

  function renderPanel (box) {
    box.querySelector('.vjs-vera-panel').innerHTML = `
      ${resumeHTML(resumable)}
      ${previousHTML(resumable)}
      <div class="vsd-vera-panel__footer">
        <button type="button" class="vsd-vera-panel__new vjs-vera-new-search"><span class="flex grow-0 shrink-0 basis-32 justify-center"><img src="${plusIcon}" width="16" height="16" alt=""></span>${t('resume.new_search')}</button>
      </div>
    `
  }

  return { refresh }
}

function relativeDays (iso) {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
  if (days < 1) return t('resume.today')
  if (days === 1) return t('resume.yesterday')
  return t('resume.days_ago', { count: days })
}

function resumeHTML (resumable) {
  if (resumable === null) return resumeLoadingHTML()

  const [conversation] = resumable
  if (!conversation) return ''

  const waiting = conversation.result_count ? `${pluralT('resume.waiting', conversation.result_count)} · ` : ''

  return `
    <button type="button" class="vsd-vera-panel__resume vjs-vera-continue" data-conversation-id="${conversation.id}" data-entry-point="continue">
      <span class="vsd-vera-panel__resume-mark"><img src="${arrowIcon}" alt=""></span>
      <span class="flex flex-col gap-2 min-w-0">
        <span class="vsd-vera-panel__resume-title">${escapeHTML(t('resume.continue', { title: conversation.title }))}</span>
        <span class="vsd-vera-panel__resume-meta">${waiting}${relativeDays(conversation.last_activity_at)}</span>
      </span>
    </button>
  `
}

// Continue already offers the latest; up to three older ones follow.
function previousHTML (resumable) {
  const [latest, ...older] = resumable || []
  const previous = older.filter(conversation => conversation.title !== latest.title).slice(0, 3)
  if (previous.length === 0) return ''

  return `
    <ul class="list-none m-0 px-0 pb-0 pt-4">
      ${previous.map(conversation => `
        <li>
          <button type="button" class="vsd-vera-panel__previous-item vjs-vera-continue" data-conversation-id="${conversation.id}" data-entry-point="continue_previous">
            <span class="flex grow-0 shrink-0 basis-32 justify-center"><img src="${clockIcon}" width="16" height="16" alt=""></span>
            <span class="vsd-vera-panel__previous-title">${escapeHTML(conversation.title)}</span>
            <span class="vsd-vera-panel__previous-meta">${relativeDays(conversation.last_activity_at)}</span>
          </button>
        </li>
      `).join('')}
    </ul>
  `
}

function resumeLoadingHTML () {
  return `
    <div class="vsd-vera-panel__resume vsd-vera-panel__resume--loading" aria-busy="true">
      <span class="vsd-vera-panel__resume-mark"><img src="${arrowIcon}" alt=""></span>
      <span class="vsd-vera-status__dots"><i></i><i></i><i></i></span>
    </div>
  `
}
